/**
 * Q AI Prompt Management API Service
 * Handles all prompt management CRUD operations for admin panel
 *
 * Server: FIX Engine (baseURL)
 * Auth: JWT Bearer token with Admin/MasterAdmin role
 *
 * Endpoints:
 * - GET    /api/admin/qai/prompts              - List all prompts
 * - GET    /api/admin/qai/prompts/{key}        - Get single prompt
 * - POST   /api/admin/qai/prompts              - Create new prompt
 * - PUT    /api/admin/qai/prompts/{key}        - Update prompt
 * - DELETE /api/admin/qai/prompts/{key}        - Delete prompt (soft)
 * - GET    /api/admin/qai/prompts/{key}/versions    - Get version history
 * - POST   /api/admin/qai/prompts/{key}/restore/{v} - Restore previous version
 * - POST   /api/admin/qai/prompts/test         - Test prompt with AI
 * - POST   /api/admin/qai/prompts/preview      - Preview with variable substitution
 * - GET    /api/admin/qai/prompts/export       - Export all prompts as JSON
 * - POST   /api/admin/qai/prompts/bulk-import  - Bulk import prompts from JSON
 */

import axios from 'axios';
import { getQAIAdminConfig } from '../config';

// Get routing configuration (DataFeed direct or FIX Engine proxy)
const { baseUrl, pathPrefix } = getQAIAdminConfig();

// ═══════════════════════════════════════════════════════════════════════════
// HELPER - Get auth headers
// ═══════════════════════════════════════════════════════════════════════════

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// PROMPT CRUD OPERATIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get all prompts
 * @returns {Promise<{success: boolean, data: Array, error?: string}>}
 */
export const getPrompts = async () => {
    try {
        const response = await axios.get(`${baseUrl}${pathPrefix}/prompts`, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data || []
        };
    } catch (error) {
        console.error('Failed to fetch prompts:', error);
        return {
            success: false,
            data: [],
            error: error.response?.data?.message || error.message || 'Failed to fetch prompts'
        };
    }
};

/**
 * Get single prompt by key
 * @param {string} promptKey - The prompt key
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const getPromptByKey = async (promptKey) => {
    try {
        const response = await axios.get(`${baseUrl}${pathPrefix}/prompts/${promptKey}`, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to fetch prompt:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch prompt'
        };
    }
};

/**
 * Create new prompt
 * @param {object} promptData - The prompt data
 * @param {string} promptData.promptKey - Unique key (alphanumeric_underscore, max 50)
 * @param {string} promptData.promptName - Display name (max 100)
 * @param {string} promptData.promptText - The prompt content (max 10,000)
 * @param {string} promptData.description - Optional description (max 255)
 * @param {string} promptData.category - 'system' | 'context' | 'rules' | 'examples' | 'qai_chat' | 'mt5_chart'
 * @param {boolean} promptData.supportsVariables - Whether variables are supported
 * @param {boolean} promptData.isActive - Active status
 * @param {object} promptData.metadata - RAG-style metadata for intelligent prompt loading (v4)
 * @param {number} promptData.metadata.tier - Load priority tier (0=highest, 3=lowest)
 * @param {string} promptData.metadata.domain - Subject domain: identity, trading, format, system
 * @param {string} promptData.metadata.intent - Query intent: analysis, security, output, routing, greeting, error
 * @param {string} promptData.metadata.priority - Priority level: critical, high, medium, low
 * @param {string} promptData.metadata.loadCondition - When to load: always, has_data, no_data, mt5_panel, webapp
 * @param {number} promptData.metadata.tokenBudget - Estimated token cost
 * @param {string[]} promptData.metadata.tags - Searchable tags
 * @param {object} promptData.metadata.abTest - A/B test configuration (optional)
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const createPrompt = async (promptData) => {
    try {
        const response = await axios.post(`${baseUrl}${pathPrefix}/prompts`, promptData, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to create prompt:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to create prompt'
        };
    }
};

/**
 * Update existing prompt
 * @param {string} promptKey - The prompt key
 * @param {object} promptData - Updated prompt data
 * @param {string} promptData.promptName - Display name
 * @param {string} promptData.promptText - The prompt content
 * @param {string} promptData.description - Optional description
 * @param {boolean} promptData.isActive - Active status
 * @param {string} promptData.changeReason - Optional reason for version history
 * @param {object} promptData.metadata - RAG-style metadata for intelligent prompt loading (v4)
 * @param {number} promptData.metadata.tier - Load priority tier (0=highest, 3=lowest)
 * @param {string} promptData.metadata.domain - Subject domain: identity, trading, format, system
 * @param {string} promptData.metadata.intent - Query intent: analysis, security, output, routing, greeting, error
 * @param {string} promptData.metadata.priority - Priority level: critical, high, medium, low
 * @param {string} promptData.metadata.loadCondition - When to load: always, has_data, no_data, mt5_panel, webapp
 * @param {number} promptData.metadata.tokenBudget - Estimated token cost
 * @param {string[]} promptData.metadata.tags - Searchable tags
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const updatePrompt = async (promptKey, promptData) => {
    try {
        const response = await axios.put(`${baseUrl}${pathPrefix}/prompts/${promptKey}`, promptData, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to update prompt:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to update prompt'
        };
    }
};

/**
 * Delete prompt (soft delete)
 * @param {string} promptKey - The prompt key
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deletePrompt = async (promptKey) => {
    try {
        await axios.delete(`${baseUrl}${pathPrefix}/prompts/${promptKey}`, {
            headers: getAuthHeaders()
        });
        return { success: true };
    } catch (error) {
        console.error('Failed to delete prompt:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to delete prompt'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// VERSION HISTORY OPERATIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get version history for a prompt
 * @param {string} promptKey - The prompt key
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getPromptVersions = async (promptKey) => {
    try {
        const response = await axios.get(`${baseUrl}${pathPrefix}/prompts/${promptKey}/versions`, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data || []
        };
    } catch (error) {
        console.error('Failed to fetch prompt versions:', error);
        return {
            success: false,
            data: [],
            error: error.response?.data?.message || error.message || 'Failed to fetch versions'
        };
    }
};

/**
 * Restore a previous version of a prompt
 * @param {string} promptKey - The prompt key
 * @param {number} version - The version number to restore
 * @param {string} reason - Optional reason for restore
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const restorePromptVersion = async (promptKey, version, reason = '') => {
    try {
        const response = await axios.post(
            `${baseUrl}${pathPrefix}/prompts/${promptKey}/restore/${version}`,
            { reason },
            { headers: getAuthHeaders() }
        );
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to restore prompt version:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to restore version'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// TESTING OPERATIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Test a prompt with a sample message
 * @param {object} testData - Test configuration
 * @param {string} testData.promptKey - The prompt key to test
 * @param {string} testData.testMessage - The test message to send
 * @param {string} testData.simulateUserType - 'connected' | 'disconnected'
 * @param {boolean} testData.includeMarketSignals - Include sample market signals
 * @param {boolean} testData.includeStrategies - Include sample strategy data
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const testPrompt = async (testData) => {
    try {
        const response = await axios.post(`${baseUrl}${pathPrefix}/prompts/test`, testData, {
            headers: getAuthHeaders(),
            timeout: 60000 // 60 second timeout for AI response
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to test prompt:', error);

        // Handle timeout specifically
        if (error.code === 'ECONNABORTED') {
            return {
                success: false,
                error: 'Test timed out after 60 seconds. Claude API may be experiencing high load.'
            };
        }

        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to test prompt'
        };
    }
};

/**
 * Preview rendered prompt with variable substitution
 * @param {string} promptKey - The prompt key to preview
 * @param {object} variables - Sample variables for substitution
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const previewPrompt = async (promptKey, variables = {}) => {
    try {
        const response = await axios.post(`${baseUrl}${pathPrefix}/prompts/preview`, {
            promptKey,
            variables
        }, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to preview prompt:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to preview prompt'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// BULK IMPORT/EXPORT OPERATIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Export all prompts as JSON
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const exportPrompts = async () => {
    try {
        const response = await axios.get(`${baseUrl}${pathPrefix}/prompts/export`, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data || []
        };
    } catch (error) {
        console.error('Failed to export prompts:', error);
        return {
            success: false,
            data: [],
            error: error.response?.data?.message || error.message || 'Failed to export prompts'
        };
    }
};

/**
 * Bulk import prompts from JSON
 * @param {Array} prompts - Array of prompt objects to import
 * @returns {Promise<{success: boolean, data?: object, error?: string}>}
 */
export const bulkImportPrompts = async (prompts) => {
    try {
        const response = await axios.post(`${baseUrl}${pathPrefix}/prompts/bulk-import`, {
            prompts
        }, {
            headers: getAuthHeaders()
        });
        return {
            success: true,
            data: response.data.data || response.data
        };
    } catch (error) {
        console.error('Failed to bulk import prompts:', error);
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to import prompts'
        };
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Prompt categories
 */
export const PROMPT_CATEGORIES = [
    { value: 'system', label: 'System', description: 'Core AI identity and behavior' },
    { value: 'context', label: 'Context', description: 'User context and data' },
    { value: 'rules', label: 'Rules', description: 'Response rules and constraints' },
    { value: 'examples', label: 'Examples', description: 'Example responses' },
    { value: 'training', label: 'Training', description: 'Training data and examples' },
    { value: 'analysis', label: 'Analysis', description: 'Analysis frameworks' },
    // Q.AI Chat categories
    { value: 'qai_chat', label: 'Q.AI Chat', description: 'Q.AI MT5 Chat prompts' },
    { value: 'mt5_chart', label: 'MT5 Chart', description: 'MT5 Panel specific prompts' },
    // MQL5 Coding categories
    { value: 'mql5_template', label: 'MQL5 Template', description: 'EA/Indicator skeleton templates' },
    { value: 'mql5_rules', label: 'MQL5 Rules', description: 'Bug fix patterns and best practices' },
    { value: 'mql5_patterns', label: 'MQL5 Patterns', description: 'Common code patterns and snippets' }
];

/**
 * MQL5-specific categories for filtering
 */
export const MQL5_CATEGORIES = ['mql5_template', 'mql5_rules', 'mql5_patterns'];

/**
 * Q.AI Chat categories for filtering
 */
export const QAI_CHAT_CATEGORIES = ['qai_chat', 'mt5_chart'];

/**
 * Metadata tier options for RAG-style prompt loading
 * Tier 0 = Identity/Security (ALWAYS load first)
 * Tier 1 = Core Analysis (Load for trading queries)
 * Tier 2 = Formatting (Load based on output context)
 * Tier 3 = Enhancement (Conditional loading)
 */
export const METADATA_TIERS = [
    { value: 0, label: 'Tier 0 - Identity/Security', description: 'ALWAYS load first - identity lockdown, data integrity' },
    { value: 1, label: 'Tier 1 - Core Analysis', description: 'Load for trading queries - analysis frameworks' },
    { value: 2, label: 'Tier 2 - Formatting', description: 'Load based on output context - MT5/WebApp formats' },
    { value: 3, label: 'Tier 3 - Enhancement', description: 'Conditional loading - persona, branding' }
];

/**
 * Metadata domain options
 */
export const METADATA_DOMAINS = [
    { value: 'identity', label: 'Identity', description: 'Identity and security related' },
    { value: 'trading', label: 'Trading', description: 'Trading analysis and signals' },
    { value: 'format', label: 'Format', description: 'Output formatting rules' },
    { value: 'system', label: 'System', description: 'System operations and routing' },
    { value: 'persona', label: 'Persona', description: 'Personality and tone' },
    { value: 'branding', label: 'Branding', description: 'Brand messaging' }
];

/**
 * Metadata intent options
 */
export const METADATA_INTENTS = [
    { value: 'security', label: 'Security', description: 'Identity protection' },
    { value: 'analysis', label: 'Analysis', description: 'Market/trade analysis' },
    { value: 'output', label: 'Output', description: 'Response formatting' },
    { value: 'routing', label: 'Routing', description: 'Intent classification' },
    { value: 'greeting', label: 'Greeting', description: 'Welcome messages' },
    { value: 'error', label: 'Error', description: 'Error handling' },
    { value: 'persona', label: 'Persona', description: 'Personality traits' },
    { value: 'branding', label: 'Branding', description: 'Brand consistency' }
];

/**
 * Metadata priority options
 */
export const METADATA_PRIORITIES = [
    { value: 'critical', label: 'Critical', description: 'Must always load' },
    { value: 'high', label: 'High', description: 'Important for most queries' },
    { value: 'medium', label: 'Medium', description: 'Load when relevant' },
    { value: 'low', label: 'Low', description: 'Optional enhancement' }
];

/**
 * Metadata load condition options
 */
export const METADATA_LOAD_CONDITIONS = [
    { value: 'always', label: 'Always', description: 'Load for every request' },
    { value: 'has_data', label: 'Has Data', description: 'Only when user has synced trades' },
    { value: 'no_data', label: 'No Data', description: 'Only when user has NO synced trades' },
    { value: 'mt5_panel', label: 'MT5 Panel', description: 'Only for MT5 panel requests' },
    { value: 'webapp', label: 'WebApp', description: 'Only for web application requests' },
    { value: 'identity_query', label: 'Identity Query', description: 'When user asks about identity' }
];

/**
 * Default metadata values for new prompts
 */
export const DEFAULT_METADATA = {
    tier: 3,
    domain: 'system',
    intent: 'analysis',
    priority: 'medium',
    loadCondition: 'always',
    tokenBudget: 200,
    tags: [],
    abTest: null
};

/**
 * Available template variables for prompts
 */
export const PROMPT_VARIABLES = [
    { key: '{{user.tokenBalance}}', description: 'User token balance', category: 'User' },
    { key: '{{user.tokensUsedTotal}}', description: 'Total tokens used', category: 'User' },
    { key: '{{sync.tradeCount}}', description: 'Number of synced trades', category: 'Sync' },
    { key: '{{sync.lastSyncAt}}', description: 'Last sync timestamp', category: 'Sync' },
    { key: '{{sync.broker}}', description: 'Connected broker name', category: 'Sync' },
    { key: '{{signal.symbol}}', description: 'Trading pair symbol', category: 'Signal' },
    { key: '{{signal.qSignal}}', description: 'Q Signal direction', category: 'Signal' },
    { key: '{{signal.qScore}}', description: 'Q Score (0-100)', category: 'Signal' }
];

/**
 * Max lengths for validation
 */
export const PROMPT_LIMITS = {
    promptKey: 50,
    promptName: 100,
    promptText: 10000,
    description: 255,
    changeReason: 255
};

/**
 * Prompt key validation regex
 */
export const PROMPT_KEY_REGEX = /^[a-z][a-z0-9_]*$/;

export default {
    getPrompts,
    getPromptByKey,
    createPrompt,
    updatePrompt,
    deletePrompt,
    getPromptVersions,
    restorePromptVersion,
    testPrompt,
    previewPrompt,
    exportPrompts,
    bulkImportPrompts,
    PROMPT_CATEGORIES,
    MQL5_CATEGORIES,
    QAI_CHAT_CATEGORIES,
    PROMPT_VARIABLES,
    PROMPT_LIMITS,
    PROMPT_KEY_REGEX,
    // Metadata constants (v4 RAG)
    METADATA_TIERS,
    METADATA_DOMAINS,
    METADATA_INTENTS,
    METADATA_PRIORITIES,
    METADATA_LOAD_CONDITIONS,
    DEFAULT_METADATA
};
